<script lang="ts">
import type { Message } from '@smartsupp/smartsupp-message'
import { onDestroy, onMount, tick } from 'svelte'
import IconSend from 'virtual:icons/lucide/send-horizontal'
import IconClose from 'virtual:icons/lucide/x'

import AvatarGroup from '@/components/avatars/AvatarGroup.svelte'
import Button from '@/components/core/Button.svelte'
import IconButton from '@/components/core/IconButton.svelte'
import FlashMessageContainer from '@/components/flashMessage/FlashMessageContainer.svelte'
import { testIds } from '@/constants'
import WidgetPopupContent from '@/frames/popup/WidgetPopupContent.svelte'
import WidgetPopupInput from '@/frames/popup/WidgetPopupInput.svelte'
import { closePopupFrame, openMessengerFrame } from '@/modules/app'
import { readChat } from '@/modules/chat'
import { hasPopupFrameHover, popupFrameHeight } from '@/modules/frames'
import { hasContactMessage, hasQuickReplies, lastUnreadMessage, popupMessage } from '@/modules/messages'
import { widgetOptions } from '@/modules/options'
import { Key } from '@/modules/textarea'
import { t } from '@/modules/translations'
import { getHostWindow } from '@/utils/window'

import WidgetPopupAgentInfo from './WidgetPopupAgentInfo.svelte'

let ref: HTMLDivElement | undefined = $state()

const { popupTextareaEnabled, widgetBlockingOptions } = widgetOptions.getOptions()
const disableClose = widgetBlockingOptions?.disableCloseButton
const disableOpenMessenger = widgetBlockingOptions?.disableOpenMessenger

let setPopupHeight = () => ref && popupFrameHeight.set(ref.getBoundingClientRect().height)
onMount(() => {
	// Set correct height of popup frame => depends on height of content which is dynamic
	setPopupHeight()
	getHostWindow().addEventListener('visibilitychange', setPopupHeight)
})

onDestroy(() => {
	getHostWindow().removeEventListener('visibilitychange', setPopupHeight)
})

const handlePopupClick = () => {
	if (disableOpenMessenger) return
	openMessengerFrame()
	readChat()
}

const handlePopupClose = () => {
	closePopupFrame(true)
}

const handlePopupMouseEnter = () => {
	hasPopupFrameHover.set(true)
}

const handlePopupMouseLeave = () => {
	hasPopupFrameHover.set(false)
}

const onLastUnreadMessage = async () => {
	await tick()
	setPopupHeight()
}

$effect(() => {
	$lastUnreadMessage && void onLastUnreadMessage()
})
let buttonText = $derived($hasContactMessage ? 'trigger.replyButton' : 'trigger.openChatButton')
let shouldShowTextarea = $derived(
	popupTextareaEnabled && $popupMessage && !hasQuickReplies($popupMessage as Message.Message.Bot),
)
</script>

<!-- If block must be wrapped by extra div, otherwise it throws error in console: https://github.com/sveltejs/svelte/issues/6037#issuecomment-1161861663 -->
<div data-testid={testIds.popup}>
	{#if $popupMessage}
		<FlashMessageContainer />
		<div
			bind:this={ref}
			role="dialog"
			aria-modal={true}
			aria-label={$t('ariaLabel.popup')}
			tabindex={0}
			class="relative p-4 bg-white rounded-xl cursor-pointer content-focus"
			onclick={handlePopupClick}
			onkeydown={(e) => e.key === Key.Enter && handlePopupClick()}
			onmouseenter={handlePopupMouseEnter}
			onmouseleave={handlePopupMouseLeave}
		>
			<div class="flex items-center mb-2">
				{#if $popupMessage.virtualAgent || $popupMessage.agentId || $popupMessage?.trigger?.identityId}
					<WidgetPopupAgentInfo
						agentId={$popupMessage.agentId}
						identityId={$popupMessage?.trigger?.identityId}
						virtualAgent={$popupMessage.virtualAgent}
					/>
				{:else}
					<AvatarGroup />
				{/if}
			</div>
			<WidgetPopupContent />
			<div class="mt-3">
				{#if shouldShowTextarea}
					<WidgetPopupInput />
				{:else}
					<Button icon={IconSend} isBlock testId={testIds.popupButton}>{$t(buttonText)}</Button>
				{/if}
			</div>
			<div class="absolute top-2 right-2 text-gray-500">
				<IconButton
					size="lg"
					icon={IconClose}
					iconDescription={$t('tooltip.close')}
					onClick={handlePopupClose}
					testId={testIds.popupButtonClose}
					disabled={disableClose}
				/>
			</div>
		</div>
	{/if}
</div>
